'use client';

import React, { useRef } from 'react';
import useCheckoutState from '../../hooks/useCheckoutState';
import useCheckoutFormHelpers from '../../hooks/useCheckoutFormHelpers';
import useMountEffect from '../../hooks/useMountEffect';
import getThemeLabels from '../../lib/getThemeLabels';
import { getPwywPlaceholder } from '../../lib/getPwywPlaceholder';
import getPriceBillingConfig from '../../lib/getPriceBillingConfig';
import getSubscriptionIntervalWithSlash from '../../services/translation/getSubscriptionIntervalWithSlash';
import PayWhatYouWantField from '../CheckoutForm/components/PayWhatYouWantField';

/**
 * Renders the pay-what-you-want amount input inline beneath a selected PWYW price
 * option in the multi-price PricePicker, wired to the shared checkout form state.
 *
 * Single-price products don't mount the PricePicker, so their PWYW input stays in
 * CheckoutForm — see CheckoutForm.js. Both call-sites build the placeholder via
 * getPwywPlaceholder so the min/suggested behaviour is identical.
 *
 * `onSelect` (multi-price picker only) selects this option when the buyer types in
 * the input — the picker shows every PWYW option's input up-front, but only the
 * selected price feeds the shared form.payWhatYouWant.
 *
 * `showCurrency` (multi-price picker only) appends the currency code to the right
 * of the input; the single-price PWYW input leaves it off.
 */
const PayWhatYouWantPriceField = ({
  price,
  labels = {},
  onSelect,
  isSelected = true,
  showCurrency = false,
  hideLabel = false,
  inputClassName,
  suffixElementPath = 'checkout.pwyw.suffix',
}) => {
  const { form, theme, currencyLocale, updateDraftChargeDebounced, pendingPwywFocusRef } =
    useCheckoutState();
  const { setFormStateByKey } = useCheckoutFormHelpers();

  const inputRef = useRef(null);
  useMountEffect(() => {
    const pending = pendingPwywFocusRef?.current;
    if (!pending || pending.priceId !== String(price._id)) return;
    if (Date.now() - pending.ts > 1500) return;
    pendingPwywFocusRef.current = null;
    inputRef.current?.focus();
  });
  // Resolve from the theme so the PWYW labels are always present, while letting
  // an explicit `labels` prop override (e.g. dashboard preview).
  const mergedLabels = { ...getThemeLabels(theme), ...labels };

  // A recurring PWYW price is "price-framed" by rendering the billing interval as
  // a suffix, so the field reads as a rate — "[ 25.00 ] /month". A one-time (or
  // legacy synthetic) price has no interval and keeps the plain currency-code
  // suffix, gated by showCurrency.
  const billingConfig = getPriceBillingConfig(price);
  const isRecurring = price.billingType === 'recurring' && !!billingConfig?.interval;
  const framingProps = isRecurring
    ? {
        suffix: getSubscriptionIntervalWithSlash(
          billingConfig.interval,
          billingConfig.intervalCount,
          mergedLabels,
        ),
      }
    : { currency: showCurrency ? price.currency || 'usd' : undefined };

  return (
    <PayWhatYouWantField
      // The picker leads with the price's own label/description, so the field's
      // "Pay what you want" label is hidden here to avoid showing both.
      hideLabel={hideLabel}
      inputClassName={inputClassName}
      innerRef={inputRef}
      suffixElementPath={suffixElementPath}
      {...framingProps}
      placeholder={getPwywPlaceholder({
        minAmount: price.amount ?? 0,
        suggestedAmount: price.pwywSuggestedPrice,
        currency: price.currency || 'usd',
        locale: currencyLocale,
        labels: mergedLabels,
        showPrice: theme?.elements?.checkout?.form?.pwyw?.minPrice?.enabled !== false,
      })}
      // The picker shows an input on every PWYW option, but the form holds a
      // single payWhatYouWant for the chosen price. Only the selected option
      // shows that value — unselected options read empty so typing in one never
      // appears in the others. (Single-PWYW callers default isSelected=true.)
      value={isSelected ? form.payWhatYouWant : ''}
      onChange={value => {
        // Typing selects this option so the amount applies to it (the input is
        // shown for every PWYW option, not just the selected one).
        onSelect?.();
        setFormStateByKey('payWhatYouWant', value);
        updateDraftChargeDebounced();
      }}
      onBlur={updateDraftChargeDebounced}
      // The single PWYW heading is the price's own label, with no default — an
      // empty label renders no heading (PayWhatYouWantField gates on the text).
      // The multi-price picker passes hideLabel, so this only shows for the
      // single-price field.
      payWhatYouWantText={price.label}
    />
  );
};

export default PayWhatYouWantPriceField;
